<script setup>
import {onMounted, ref, watch} from 'vue'
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import Multiselect from '@vueform/multiselect'
import '@vueform/multiselect/themes/default.css'
import {useI18n} from 'vue-i18n';

const {t} = useI18n()
const props = defineProps({
  // Pre-select declaration_owner: true for new accounts (client is owner by default)
  bank_account: {type: Object, required: false, default: {bank_account_owner: null, account_number: null, bank_code: null, is_multicurrency: false, currencies: 'CZK', iban: null, bic: null, note: null, is_primary: true, declaration_owner: true}},
});

const bankAccount = ref({...props.bank_account});

const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);
const closeModalWith = (value) => {
  isOpen.value = false;
  setTimeout(() => {
    if (value === 'confirm') {
      emit('confirm', {...bankAccount.value, currencies: bankAccount.value.is_multicurrency ? Array.from(bankAccount.value.currencies) : [bankAccount.value.currencies]});
    } else {
      emit('cancel');
    }
  }, 300);
}

const showBankAccount = ref(showBankAccountFn())
watch(bankAccount.value, (newValue) => {
    showBankAccount.value = showBankAccountFn();
}, {deep: true});

function showBankAccountFn() {
  return bankAccount.value.is_multicurrency
      ? bankAccount.value.currencies && bankAccount.value.currencies.length === 1 && bankAccount.value.currencies.includes('CZK')
      : bankAccount.value.currencies === 'CZK';
}


function openModal() {
  isOpen.value = true
}

onMounted(() => {
  if (!bankAccount.value.is_multicurrency) {
    bankAccount.value.currencies = bankAccount.value.currencies[0];
  }
  console.log(props.bank_account);
});
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div"  class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle as="h3" class="modal-title">
                {{ bankAccount.id ? t('saveClientBankAccountModal.bank_account_edit') : t('saveClientBankAccountModal.bank_account_create') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form @submit.prevent="closeModalWith('confirm')">
              <div class="modal-body">
                <div class="mb-3">
                  <div class="d-flex justify-content-between">
                  <label for="currencySelect" class="form-label">{{ t('saveClientBankAccountModal.currency') }}</label>
                    <div class="form-check">
                      <input @click="bankAccount.currencies = null" v-model="bankAccount.is_multicurrency" class="form-check-input" type="checkbox" value="" id="multicurrency">
                      <label class="form-check-label" for="multicurrency">
                        {{ t('saveClientBankAccountModal.multi_currency_account') }}
                      </label>
                    </div>
                  </div>
                  <Multiselect v-if="bankAccount.is_multicurrency" required mode="tags" v-model="bankAccount.currencies" :options="['CZK', 'EUR', 'USD']" :hide-selected="false" :placeholder="t('saveClientBankAccountModal.choose_currencies')"/>
                  <Multiselect v-else required mode="single" v-model="bankAccount.currencies" :options="['CZK', 'EUR', 'USD']" :hide-selected="false" :placeholder="t('saveClientBankAccountModal.choose_currency')"/>
                </div>
                <div class="mb-3">
                  <label for="bankAccountOwnerInput" class="form-label">{{ t('saveClientBankAccountModal.bank_account_owner_name') }}</label>
                  <input v-model="bankAccount.bank_account_owner" id="bankAccountOwnerInput" class="form-control"/>
                </div>
                <div v-if="showBankAccount" class="mb-3">
                  <label for="accountNumberInput" class="form-label">{{ t('saveClientBankAccountModal.account_number') }}</label>
                  <input v-model="bankAccount.account_number" id="accountNumberInput" class="form-control" required/>
                </div>
                <div v-if="showBankAccount" class="mb-3">
                  <label for="bankCodeInput" class="form-label">{{ t('saveClientBankAccountModal.bank_code') }}</label>
                  <input v-model="bankAccount.bank_code" id="bankCodeInput" class="form-control" required/>
                </div>
                <div v-if="!showBankAccount" class="mb-3">
                  <label for="ibanInput" class="form-label">{{ t('saveClientBankAccountModal.iban') }}</label>
                  <input v-model="bankAccount.iban" id="ibanInput" class="form-control" required/>
                </div>
                <div v-if="!showBankAccount" class="mb-3">
                  <label for="bicInput" class="form-label">{{ t('saveClientBankAccountModal.bic_swift') }}</label>
                  <input v-model="bankAccount.bic" id="bicInput" class="form-control" required/>
                </div>
                <div class="mb-3">
                  <div class="form-check">
                    <input v-model="bankAccount.is_primary" class="form-check-input" type="checkbox" id="primaryAccountInput">
                    <label class="form-check-label" for="primaryAccountInput">
                      {{ t('saveClientBankAccountModal.primary_account') }}
                    </label>
                  </div>
                </div>
                <div class="mb-3">
                  <div class="form-check">
                    <input v-model="bankAccount.declaration_owner" class="form-check-input" type="checkbox" id="declarationOwnerCheckbox">
                    <label class="form-check-label" for="declarationOwnerCheckbox">
                      {{ t('saveClientBankAccountModal.declaration_owner') }}
                    </label>
                  </div>
                </div>
                <div class="mb-3">
                  <div class="form-check">
                    <input v-model="bankAccount.declaration_spouses_property" class="form-check-input" type="checkbox" id="declarationSpousesPropertyCheckbox">
                    <label class="form-check-label" for="declarationSpousesPropertyCheckbox">
                      {{ t('saveClientBankAccountModal.declaration_spouses_property') }}
                    </label>
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('saveClientBankAccountModal.cancel') }}</button>
                <button class="btn btn-primary btn-sm" type="submit">{{ t('saveClientBankAccountModal.save') }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

